import { IElementWaypoint } from "@/hooks/use-placeholders";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector } from "@/store/store-hooks";
import {
  IElementsState,
  IMapPlaceholderVariants,
  isMapPlaceholdersUpdated,
  MapWaypointsRendererRef,
  selectAncestor,
  selectIElement,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementImg360,
  isIElementImg2d,
  isIElementImg360,
  isIElementRoomSection,
  isIElementSectionFlash,
  isIElementTimeSeriesRoom,
} from "@faro-lotv/ielement-types";
import { MapPlaceholders, PlaceholdersTexture } from "@faro-lotv/lotv";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect } from "react";
import { Vector3 } from "three";
import { selectImgsFromRoom } from "../mode-selectors";
import {
  useEffectiveFlashTextures,
  useWaypointsTextures,
} from "./use-waypoints-textures";

const FLASH_SCANS_LABEL = "Flash Scans";
const PHOTO_2D = "PHOTO_2D";
const PANO_AND_PHOTO = "PANO_PHOTO";
const LOCATION_MARKER = "LOCATION_MARKER";

/** Size of the default flash scan icon, in screen pixels without accounting for DPR */
const FLASH_SCANS_DEFAULT_SZ = 24;

/** Size of the hovered flash scan icon, in screen pixels without accounting for DPR */
const FLASH_SCANS_HOVERED_SZ = 34;

/** Size of the default waypoint icon, in screen pixels without accounting for DPR */
const WAYPOINT_DEFAULT_SZ = 20;
/** Size of the hovered waypoint icon, in screen pixels without accounting for DPR */
const WAYPOINT_HOVERED_SZ = 24;

/**
 * This hook ensures that, at any point in time, the waypoints'textures correctly match their labels.
 *
 * @param mapPlaceholders Reference to the object rendering the waypoints
 * @param shiftedPlaceholders The positions of the waypoints, after applying the offset
 * @param visiblePlaceholders The list of visible placeholders
 * @param flashTextures Textures to apply to flash scans waypoints
 */
export function useApplyTexturesToLabelWaypoints(
  mapPlaceholders: MapPlaceholders | null | undefined,
  shiftedPlaceholders: Vector3[],
  visiblePlaceholders: IElementWaypoint[],
  flashTextures?: Partial<IMapPlaceholderVariants>,
): void {
  const effectiveFlashTextures = useEffectiveFlashTextures(flashTextures);

  const applyTextures = useCallback(
    (
      map: MapWaypointsRendererRef,
      label: string,
      textures: Partial<IMapPlaceholderVariants>,
      sizes: { default: number; hovered: number; selected: number },
    ) => {
      if (textures.defaultTexture) {
        map.setLabeledMap(
          label,
          PlaceholdersTexture.Default,
          textures.defaultTexture,
        );
      }
      if (textures.hoveredTexture) {
        map.setLabeledMap(
          label,
          PlaceholdersTexture.Hovered,
          textures.hoveredTexture,
        );
      }
      if (textures.selectedTexture) {
        map.setLabeledMap(
          label,
          PlaceholdersTexture.Selected,
          textures.selectedTexture,
        );
      }

      map.setLabelSizes(label, sizes);
    },
    [],
  );

  const { panoImgIndex, imgIndex, locationIndex } = useAppSelector(
    selectIndexes(visiblePlaceholders),
  );

  const { panoImgTextures, imgTextures, markerTextures } =
    useWaypointsTextures();

  const flashScanIds = useAppSelector(
    selectFlashScanIds(visiblePlaceholders),
    isEqual,
  );

  useEffect(() => {
    if (!isMapPlaceholdersUpdated(mapPlaceholders, shiftedPlaceholders)) {
      return;
    }

    // Flash scan icons
    mapPlaceholders.setLabeledPlacehoders(FLASH_SCANS_LABEL, flashScanIds);
    applyTextures(
      mapPlaceholders,
      FLASH_SCANS_LABEL,
      effectiveFlashTextures,
      labelSizes(FLASH_SCANS_DEFAULT_SZ, FLASH_SCANS_HOVERED_SZ),
    );

    mapPlaceholders.setLabeledPlacehoders(LOCATION_MARKER, locationIndex);
    applyTextures(
      mapPlaceholders,
      LOCATION_MARKER,
      markerTextures,
      labelSizes(WAYPOINT_DEFAULT_SZ, WAYPOINT_HOVERED_SZ),
    );

    // 2D Photos
    mapPlaceholders.setLabeledPlacehoders(PHOTO_2D, imgIndex);
    applyTextures(
      mapPlaceholders,
      PHOTO_2D,
      imgTextures,
      labelSizes(WAYPOINT_DEFAULT_SZ, WAYPOINT_HOVERED_SZ),
    );

    mapPlaceholders.setLabeledPlacehoders(PANO_AND_PHOTO, panoImgIndex);
    applyTextures(
      mapPlaceholders,
      PANO_AND_PHOTO,
      panoImgTextures,
      labelSizes(WAYPOINT_DEFAULT_SZ, WAYPOINT_HOVERED_SZ),
    );
  }, [
    mapPlaceholders,
    flashScanIds,
    shiftedPlaceholders,
    imgIndex,
    imgTextures,
    panoImgIndex,
    panoImgTextures,
    applyTextures,
    effectiveFlashTextures,
    locationIndex,
    markerTextures,
  ]);
}

/**
 * helper function to compute the sizes of the labels used for waypoints
 *
 * @returns the sizes of the labels used for waypoints
 * @param defaultSize The default size of the label
 * @param hoveredSize The size of the label when hovered
 * @param dpr The device pixel ratio
 */
function labelSizes(
  defaultSize: number,
  hoveredSize: number,
): { default: number; hovered: number; selected: number } {
  // for all labels hovered and selected sizes are the same
  return {
    default: defaultSize,
    hovered: hoveredSize,
    selected: hoveredSize,
  };
}

const selectIndexes = curryAppSelector(
  createSelector(
    [selectIElementsSlice, (_state, panoImgs: IElementWaypoint[]) => panoImgs],
    (
      iElements: IElementsState,
      panoImgs,
    ): {
      panoImgIndex: number[];
      imgIndex: number[];
      locationIndex: number[];
    } => {
      const panoImgIndex: number[] = [];
      const imgIndex: number[] = [];
      const locationIndex: number[] = [];
      panoImgs.forEach((el, index) => {
        if (isIElementImg2d(el)) {
          imgIndex.push(index);
        } else if (isIElementRoomSection(el)) {
          locationIndex.push(index);
        } else if (
          isIElementImg360(el) &&
          selectIsPanoWithImg2d(el)({ iElements })
        ) {
          panoImgIndex.push(index);
        }
      });
      return {
        panoImgIndex,
        imgIndex,
        locationIndex,
      };
    },
  ),
);

function selectIsPanoWithImg2d(pano: IElementImg360) {
  return ({ iElements }: { iElements: IElementsState }) => {
    const timeseries = selectAncestor(
      pano,
      isIElementTimeSeriesRoom,
    )({ iElements });
    if (!timeseries) return false;
    const roomSection = selectIElement(timeseries.parentId)({ iElements });
    if (!roomSection || !isIElementRoomSection(roomSection)) return false;

    return selectImgsFromRoom(roomSection)({ iElements }).length > 0;
  };
}

/**
 * @returns Which panos of the input list are flash scans
 * @param panoImgs The list of panoImgs to filter
 */
function selectFlashScanIds(panoImgs: IElementWaypoint[]) {
  return (state: OnlyIElementsStore) => {
    const ret: number[] = [];

    for (let panoId = 0; panoId < panoImgs.length; panoId++) {
      const pano = panoImgs[panoId];
      const isFlash =
        isIElementImg360(pano) &&
        !!selectAncestor(pano, isIElementSectionFlash)(state);
      if (isFlash) ret.push(panoId);
    }

    return ret;
  };
}
